/**
 * Push Subscription Database Operations
 *
 * Manages Web Push subscriptions stored in Supabase.
 */

import { supabase } from '@/lib/supabaseClient';

export interface PushSubscriptionRecord {
  id: string;
  userId: string;
  endpoint: string;
  auth: string;
  p256dh: string;
  expiresAt: string | null;
  createdAt: string;
}

/**
 * Save or update a push subscription for the current user.
 * Uses upsert on (user_id, endpoint) to avoid duplicates.
 */
export async function savePushSubscription(
  userId: string,
  subscription: PushSubscriptionJSON
): Promise<void> {
  const keys = subscription.keys || {};
  const { error } = await supabase
    .from('push_subscriptions')
    .upsert(
      {
        user_id: userId,
        endpoint: subscription.endpoint,
        auth: keys.auth || '',
        p256dh: keys.p256dh || '',
        expires_at: subscription.expirationTime
          ? new Date(subscription.expirationTime).toISOString()
          : null,
      },
      { onConflict: 'user_id,endpoint' }
    );

  if (error) throw error;
}

/**
 * Remove a push subscription by endpoint.
 */
export async function removePushSubscription(
  userId: string,
  endpoint: string
): Promise<void> {
  const { error } = await supabase
    .from('push_subscriptions')
    .delete()
    .eq('user_id', userId)
    .eq('endpoint', endpoint);

  if (error) throw error;
}

/**
 * Remove all push subscriptions for a user (e.g. on logout).
 */
export async function removeAllPushSubscriptions(userId: string): Promise<void> {
  const { error } = await supabase
    .from('push_subscriptions')
    .delete()
    .eq('user_id', userId);

  if (error) throw error;
}

/**
 * Get all push subscriptions for a user.
 */
export async function getPushSubscriptions(
  userId: string
): Promise<PushSubscriptionRecord[]> {
  const { data, error } = await supabase
    .from('push_subscriptions')
    .select('*')
    .eq('user_id', userId);

  if (error) throw error;
  return (data || []).map((row) => ({
    id: row.id,
    userId: row.user_id,
    endpoint: row.endpoint,
    auth: row.auth,
    p256dh: row.p256dh,
    expiresAt: row.expires_at,
    createdAt: row.created_at,
  }));
}
